/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import PasswordInput from './PasswordInput';
import {
  useLoading,
  useIsAuthorized,
  useShowLoginModal,
  useUserInfo,
  useStation,
  useImpersonatedUserId
} from '../contexts/GlobalStateContext';
import ChangePassword from './ChangePassword';
import BattenburgLoader from '../common/BattenburgLoader';
import { fetchWithAuth } from '../services/api';

const LoginOutModalForm = () => {
  const { isAuthorized, setIsAuthorized } = useIsAuthorized();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captionText, setCaptionText] = useState('Logga in');
  const { loading, setLoading } = useLoading();
  const { setStation } = useStation();
  const { userInfo, setUserInfo } = useUserInfo();
  const { setImpersonatedUserId } = useImpersonatedUserId();
  const { setShowLoginModal } = useShowLoginModal();
  const navigate = useNavigate();
  const isStandard = userInfo?.role === 'Standard';
  const timeoutPtr = useRef(null);

  const checkPasswordOnServerAsync = async (password, username) => {
    try {
      const response = await fetch('/api/authorization/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Login error:', error);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  };

  const checkPasswordAsync = async (password, username) => {
    setLoading(true);
    try {
      const response = await checkPasswordOnServerAsync(password, username);

      if (response && response.token) {
        // 🟢 Spara i sessionStorage istället för sessionStorage
        sessionStorage.setItem('jwt_token', response.token);
        sessionStorage.setItem('user_id', response.userId);
        sessionStorage.setItem('username', response.username);
        sessionStorage.setItem('email', response.email);
        sessionStorage.setItem('user_role', response.role || 'Standard');

        // Uppdatera state
        setIsAuthorized(true);

        setTimeout(() => {
          setUserInfo({
            userId: response.userId,
            username: response.username,
            email: response.email,
            role: response.role || 'Standard',
          });

          setShowLoginModal(false);
          setCaptionText('Logga in');
          setUsername('');
          setPassword('');

          // Navigera baserat på roll
          if (response.role === 'Standard') {
            navigate("/scheman");
          } else {
            navigate('/');
          }
        }, 50); // Försening för att säkerställa att state uppdateras före navigering

        return 'PasswordOk';
      } else {
        setCaptionText('Fel användarnamn eller lösenord');
        return 'PasswordIncorrect';
      }
    } catch (error) {
      console.error(error);
      setCaptionText('Inloggning misslyckades. Försök igen.');
      return 'PasswordIncorrect';
    } finally {
      setLoading(false);
    }
  };

  const logOutUserAsync = async () => {
    const response = await fetchWithAuth('/api/authorization/logout', {
      method: 'POST',
    });

    if (!response.ok) throw new Error('Logout failed');

    return await response.json();
  };

  const logOutAsync = async () => {
    setLoading(true);
    try {
      await logOutUserAsync();
      if (isStandard) {
        setStation("---");
      }

      setImpersonatedUserId("");
      setUserInfo(null);
      sessionStorage.clear();

      setTimeout(() => {
        setIsAuthorized(false);
      }, 20);

      return 'userLoggedOut';
    } catch (error) {
      console.error(error);
      sessionStorage.clear();
      setIsAuthorized(false);
      setUserInfo(null);
      return 'Error';
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setCaptionText('Logga in');
    setShowLoginModal(false);
  };

  const delayedHandleLogInOut = (e) => {
    if (e?.preventDefault) e.preventDefault(); // ← detta saknas

    if (isAuthorized) {
      timeoutPtr.current = setTimeout(() => {
        handleClose();
        navigate('/');
      }, 5000);
    }

    handleLogInOut(e); // ← skicka med e så handleLogInOut kan anropa e.preventDefault också
  }

  const handleLogInOut = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      if (isAuthorized) {
        const response = await logOutAsync();
        if (response === 'userLoggedOut' || response === 'userAlreadyLoggedOut') {
          if (timeoutPtr.current) {
            clearTimeout(timeoutPtr.current);
            timeoutPtr.current = null;
          }

          handleClose();
          navigate('/');
        }
      } else {
        if (!username.trim()) {
          setCaptionText('Ange användarnamn');
          setLoading(false);
          return;
        }
        if (!password) {
          setCaptionText('Ange lösenord');
          setLoading(false);
          return;
        }

        const response = await checkPasswordAsync(password, username);
        if (response === 'PasswordOk') handleClose();
      }
    } catch (error) {
      console.error('Error:', error);
      setCaptionText('Ett fel uppstod. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      SystemAdmin: (
        <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-red-600 tw-text-white">
          SystemAdmin
        </span>
      ),
      Admin: (
        <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-blue-600 tw-text-white">
          Admin
        </span>
      ),
      Standard: (
        <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-gray-600 tw-text-white">
          Standard
        </span>
      ),
    };
    return badges[role] || null;
  };

  return (
    <Modal labelledById="modal-title" onClose={handleClose}>
      <form onSubmit={delayedHandleLogInOut}>
        <div className="tw-flex tw-flex-col">
          <div className="tw-text-lg tw-font-semibold" id="modal-title">
            {!isAuthorized ? (
              <>
                <PasswordInput
                  text={username}
                  type="text"
                  placeholder="Användarnamn"
                  preText={captionText}
                  onTextChanged={setUsername}
                  autoFocus
                />
                <PasswordInput
                  text={password}
                  type="password"
                  placeholder="Lösenord"
                  preText=""
                  onTextChanged={setPassword}
                />
              </>
            ) : (
              <div>
                <div className="tw-flex tw-items-center">
                  <strong>Inloggad som: {userInfo?.username}</strong>
                  {userInfo?.role && getRoleBadge(userInfo.role)}
                </div>
                <p className="tw-text-sm tw-text-gray-600 tw-mt-1">{userInfo?.email}</p>
              </div>
            )}
          </div>

          <div className="tw-flex tw-justify-start tw-gap-2 tw-mt-4 tw-mb-4">
            <button
              type="button"
              onClick={handleClose}
              className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-gray-600 tw-text-white hover:tw-bg-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400"
            >
              {isAuthorized ? 'Stäng' : 'Avbryt'}
            </button>

            <button
              type={isAuthorized ? 'button' : 'submit'}
              onClick={isAuthorized ? delayedHandleLogInOut : undefined}
              disabled={loading || (!isAuthorized && (!username.trim() || !password))}
              className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-white focus:tw-outline-none focus:tw-ring-2 
                ${loading || (!isAuthorized && (!username.trim() || !password))
                  ? 'tw-bg-gray-400 tw-cursor-not-allowed'
                  : isAuthorized
                    ? 'tw-bg-red-600 hover:tw-bg-red-700 focus:tw-ring-red-400'
                    : 'tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-ring-blue-400'
                }`}
            >
              {isAuthorized ? 'Logga ut' : 'Logga in'}
            </button>
          </div>

          {isAuthorized && (
            <div>
              <ChangePassword />
            </div>
          )}

          <div className="tw-flex tw-justify-center tw-min-h-[40px]">
            {loading && <BattenburgLoader width={100} left="52%" />}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default LoginOutModalForm;
